import { useMemo, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Link, useNavigate } from "react-router-dom";
import { FilterDropdown } from "@/components/ui/filter-dropdown";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { FirmLogo } from "@/components/firm-logo";
import {
  DataTableShell,
  DataTableHeader,
  DataTableRow,
  DataTableCell,
  tableRowVariants,
} from "@/components/ui/data-table";
import { Check, X as XIcon, Circle } from "@/components/ui/icons";
import { cn } from "@/lib/utils";
import {
  categorizeAccount,
  CATEGORY_ORDER,
  CATEGORY_LABEL,
  type ChallengeCategory,
  type GroupableAccount,
} from "@/lib/account-groups";
import type { AccountCardData } from "@/components/account-card";

/**
 * View-model consumed by the table. Mirrors the shape Accounts.tsx
 * already builds; kept here so the page + table share a single source
 * of truth and the table's filter logic can lean on the same fields.
 */
export interface AccountView extends GroupableAccount {
  data: AccountCardData;
  tableStatus: "active" | "paused" | "passed" | "breached";
  rulesConfirmedAt: number | null;
  slug: string;
  id: string;
  firmName: string;
  firmLogo: string;
  accountSize: number;
  balance: number | null;
  challengeType: string | null;
  platformType: string | null;
  dailyLossPct: number | null;
  maxDrawdownPct: number | null;
  profitTargetPct: number | null;
  pairSlug: string | null;
}

interface AccountsTableProps {
  accounts: AccountView[];
  matchingId: string | null;
  onFindPartner: (acc: AccountView) => void;
}

const PAGE_SIZE = 20;

const STATUS_ORDER = ["active", "paused", "passed", "breached"] as const;
const STATUS_LABEL: Record<(typeof STATUS_ORDER)[number], string> = {
  active: "Active",
  paused: "Paused",
  passed: "Passed",
  breached: "Breached",
};

const PHASE_LABEL: Record<string, string> = {
  step1: "Step 1",
  step2: "Step 2",
  step3: "Step 3",
  funded: "Funded",
};

function formatSize(n: number): string {
  if (!n) return "—";
  if (n >= 1000) return `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}K`;
  return `$${n}`;
}

function formatBalance(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function phaseBadgeVariant(
  phase: string,
): "step1" | "step2" | "funded" | "warning" {
  if (phase === "step1") return "step1";
  if (phase === "step2") return "step2";
  if (phase === "funded") return "funded";
  return "warning";
}

function StatusBadge({
  status,
  label,
}: {
  status: AccountView["tableStatus"];
  label?: string;
}): ReactNode {
  return (
    <Badge variant={status} size="xs">
      {status === "breached" ? (
        <XIcon className="size-2.5" strokeWidth={3} />
      ) : status === "passed" ? (
        <Check className="size-2.5" strokeWidth={3} />
      ) : (
        <Circle className="size-2 fill-current" strokeWidth={0} />
      )}
      {label ?? STATUS_LABEL[status]}
    </Badge>
  );
}

export function AccountsTable({
  accounts,
  matchingId,
  onFindPartner,
}: AccountsTableProps): ReactNode {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [firmFilter, setFirmFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const firms = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of accounts) map.set(a.firmName, a.firmName);
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [accounts]);

  const categories = useMemo(() => {
    const present = new Set<ChallengeCategory>();
    for (const a of accounts) {
      present.add(
        categorizeAccount({
          challenge_type: a.challengeType,
          steps: a.steps,
          platform_type: a.platformType ?? "",
        }),
      );
    }
    return CATEGORY_ORDER.filter((c) => present.has(c)).map((c) => ({
      value: c,
      label: CATEGORY_LABEL[c],
    }));
  }, [accounts]);

  const phases = useMemo(() => {
    const set = new Set<string>();
    for (const a of accounts) set.add(a.phase);
    return Array.from(set)
      .filter((p) => PHASE_LABEL[p])
      .sort((a, b) => (PHASE_LABEL[a] ?? a).localeCompare(PHASE_LABEL[b] ?? b))
      .map((p) => ({ value: p, label: PHASE_LABEL[p] ?? p }));
  }, [accounts]);

  const statuses = useMemo(() => {
    const present = new Set<AccountView["tableStatus"]>();
    for (const a of accounts) present.add(a.tableStatus);
    return STATUS_ORDER.filter((s) => present.has(s)).map((s) => ({
      value: s,
      label: STATUS_LABEL[s],
    }));
  }, [accounts]);

  const filtered = useMemo(() => {
    return accounts.filter((a) => {
      if (firmFilter !== "all" && a.firmName !== firmFilter) return false;
      if (categoryFilter !== "all") {
        const cat = categorizeAccount({
          challenge_type: a.challengeType,
          steps: a.steps,
          platform_type: a.platformType ?? "",
        });
        if (cat !== categoryFilter) return false;
      }
      if (phaseFilter !== "all" && a.phase !== phaseFilter) return false;
      if (statusFilter !== "all" && a.tableStatus !== statusFilter) return false;
      return true;
    });
  }, [accounts, firmFilter, categoryFilter, phaseFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page],
  );

  const setFirmAndReset = (v: string) => {
    setFirmFilter(v);
    setPage(1);
  };
  const setCategoryAndReset = (v: string) => {
    setCategoryFilter(v);
    setPage(1);
  };
  const setPhaseAndReset = (v: string) => {
    setPhaseFilter(v);
    setPage(1);
  };
  const setStatusAndReset = (v: string) => {
    setStatusFilter(v);
    setPage(1);
  };

  const hasActiveFilter =
    firmFilter !== "all" ||
    categoryFilter !== "all" ||
    phaseFilter !== "all" ||
    statusFilter !== "all";

  const clearFilters = () => {
    setFirmFilter("all");
    setCategoryFilter("all");
    setPhaseFilter("all");
    setStatusFilter("all");
    setPage(1);
  };

  return (
    <div>
      {/* ── Filters ─────────────────────────────────────── */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <FilterDropdown
            label="Firm"
            value={firmFilter}
            options={firms}
            onValueChange={setFirmAndReset}
            allLabel="All Firms"
          />
          <FilterDropdown
            label="Type"
            value={categoryFilter}
            options={categories}
            onValueChange={setCategoryAndReset}
            allLabel="All Types"
          />
          <FilterDropdown
            label="Phase"
            value={phaseFilter}
            options={phases}
            onValueChange={setPhaseAndReset}
            allLabel="All Phases"
          />
          <FilterDropdown
            label="Status"
            value={statusFilter}
            options={statuses}
            onValueChange={setStatusAndReset}
            allLabel="All Statuses"
          />
        </div>

        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <AnimatePresence>
            {hasActiveFilter && (
              <motion.button
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                onClick={clearFilters}
                className="text-xs font-medium text-accent transition-colors hover:text-accent/80"
              >
                Clear All
              </motion.button>
            )}
          </AnimatePresence>
          <span className="whitespace-nowrap text-xs text-muted-foreground">
            {filtered.length} Account{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* ── Active filter chips ─────────────────────────── */}
      {hasActiveFilter && (
        <div className="mb-4 flex flex-wrap gap-2">
          {firmFilter !== "all" && (
            <Badge
              variant="outline"
              className="h-auto cursor-pointer gap-1 border-accent/20 bg-accent/5 px-2.5 py-1 text-xs text-accent transition-colors hover:bg-accent/10"
              onClick={() => setFirmAndReset("all")}
            >
              {firms.find((f) => f.value === firmFilter)?.label} ×
            </Badge>
          )}
          {categoryFilter !== "all" && (
            <Badge
              variant="outline"
              className="h-auto cursor-pointer gap-1 border-accent/20 bg-accent/5 px-2.5 py-1 text-xs text-accent transition-colors hover:bg-accent/10"
              onClick={() => setCategoryAndReset("all")}
            >
              {categories.find((c) => c.value === categoryFilter)?.label} ×
            </Badge>
          )}
          {phaseFilter !== "all" && (
            <Badge
              variant="outline"
              className="h-auto cursor-pointer gap-1 border-accent/20 bg-accent/5 px-2.5 py-1 text-xs text-accent transition-colors hover:bg-accent/10"
              onClick={() => setPhaseAndReset("all")}
            >
              {PHASE_LABEL[phaseFilter] ?? phaseFilter} ×
            </Badge>
          )}
          {statusFilter !== "all" && (
            <Badge
              variant="outline"
              className="h-auto cursor-pointer gap-1 border-accent/20 bg-accent/5 px-2.5 py-1 text-xs text-accent transition-colors hover:bg-accent/10"
              onClick={() => setStatusAndReset("all")}
            >
              {STATUS_LABEL[statusFilter as AccountView["tableStatus"]] ??
                statusFilter}{" "}
              ×
            </Badge>
          )}
        </div>
      )}

      {/* ── Desktop table ───────────────────────────────── */}
      <DataTableShell minWidth={980}>
        <DataTableHeader
          labels={[
            "Account",
            "Firm",
            "Size",
            "Balance",
            "Type",
            "Phase",
            "Status",
            "Rules",
            "Pair",
          ]}
        />
        <tbody>
          {paged.map((acc, i) => {
            const archived = acc.outcome != null;
            const displayBalance = archived
              ? acc.data.finalBalance ?? acc.balance
              : acc.balance;
            return (
              <DataTableRow
                key={acc.id}
                index={i}
                muted={archived}
                className="cursor-pointer"
                onClick={() =>
                  navigate(`/accounts/${encodeURIComponent(acc.slug)}`)
                }
              >
                {/* Account */}
                <DataTableCell align="left" className="px-4">
                  <div className="flex min-w-0 items-center gap-2">
                    {acc.rulesConfirmedAt == null && !archived && (
                      <span
                        title="Rules not yet confirmed"
                        className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                      />
                    )}
                    <Link
                      to={`/accounts/${encodeURIComponent(acc.slug)}`}
                      className="truncate font-mono text-xs font-medium text-foreground hover:text-accent"
                      onClick={(e) => e.stopPropagation()}
                    >
                      #{acc.slug}
                    </Link>
                    {acc.data.accountLabel && (
                      <span className="truncate rounded-full bg-muted/40 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {acc.data.accountLabel}
                      </span>
                    )}
                  </div>
                </DataTableCell>

                {/* Firm */}
                <DataTableCell>
                  <div className="flex items-center justify-center gap-2">
                    <FirmLogo
                      src={acc.firmLogo}
                      name={acc.firmName}
                      className="h-5 w-5 shrink-0"
                    />
                    <span className="max-w-[140px] truncate text-xs text-foreground">
                      {acc.firmName}
                    </span>
                  </div>
                </DataTableCell>

                {/* Size */}
                <DataTableCell className="text-sm font-semibold text-foreground">
                  {formatSize(acc.accountSize)}
                </DataTableCell>

                {/* Balance */}
                <DataTableCell>
                  {displayBalance != null ? (
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          "font-mono text-sm",
                          archived
                            ? "text-muted-foreground"
                            : "font-semibold text-accent",
                        )}
                      >
                        {formatBalance(displayBalance)}
                      </span>
                      {archived && (
                        <span className="mt-0.5 text-[10px] uppercase tracking-wider text-muted-foreground/60">
                          Final
                        </span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </DataTableCell>

                {/* Type */}
                <DataTableCell className="text-xs text-muted-foreground">
                  {
                    CATEGORY_LABEL[
                      categorizeAccount({
                        challenge_type: acc.challengeType,
                        steps: acc.steps,
                        platform_type: acc.platformType ?? "",
                      })
                    ]
                  }
                </DataTableCell>

                {/* Phase */}
                <DataTableCell>
                  <Badge variant={phaseBadgeVariant(acc.phase)} size="xs">
                    {PHASE_LABEL[acc.phase] ?? acc.phase}
                  </Badge>
                </DataTableCell>

                {/* Status */}
                <DataTableCell>
                  <StatusBadge
                    status={acc.tableStatus}
                    label={acc.data.statusLabel}
                  />
                </DataTableCell>

                {/* Rules */}
                <DataTableCell>
                  {archived ? (
                    <span className="text-xs text-muted-foreground/60">—</span>
                  ) : acc.rulesConfirmedAt != null ? (
                    <Badge variant="passed" size="xs" title="Rules confirmed by operator">
                      <Check className="size-2.5" strokeWidth={3} />
                      Confirmed
                    </Badge>
                  ) : (
                    <Badge variant="warning" size="xs" title="Rules not yet confirmed — open the account to fill them in.">
                      Pending
                    </Badge>
                  )}
                </DataTableCell>

                {/* Pair */}
                <DataTableCell className="px-4">
                  {acc.pairSlug ? (
                    <Link
                      to={`/pairs/${encodeURIComponent(acc.pairSlug)}`}
                      className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 font-mono text-[10px] text-accent hover:bg-accent/20"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {acc.pairSlug}
                    </Link>
                  ) : !archived &&
                    acc.rulesConfirmedAt != null &&
                    acc.tableStatus === "active" ? (
                    <button
                      type="button"
                      className="rounded-full border border-accent/40 bg-accent/5 px-2 py-0.5 text-[10px] font-medium text-accent transition-colors hover:bg-accent/10 disabled:opacity-60"
                      onClick={(e) => {
                        e.stopPropagation();
                        onFindPartner(acc);
                      }}
                      disabled={matchingId === acc.id}
                    >
                      {matchingId === acc.id ? "Matching…" : "Find Partner"}
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </DataTableCell>

              </DataTableRow>
            );
          })}
        </tbody>
      </DataTableShell>

      {/* ── Mobile cards ────────────────────────────────── */}
      <div className="flex flex-col gap-3 lg:hidden">
        {paged.map((acc, i) => {
          const archived = acc.outcome != null;
          const displayBalance = archived
            ? acc.data.finalBalance ?? acc.balance
            : acc.balance;
          const cat = categorizeAccount({
            challenge_type: acc.challengeType,
            steps: acc.steps,
            platform_type: acc.platformType ?? "",
          });
          return (
            <motion.div
              key={acc.id}
              custom={i}
              variants={tableRowVariants}
              initial="hidden"
              animate="visible"
              whileHover={{
                y: -3,
                transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
              }}
              onClick={() =>
                navigate(`/accounts/${encodeURIComponent(acc.slug)}`)
              }
              className={cn(
                "block cursor-pointer rounded-xl border border-border bg-card p-4 transition-[border-color,box-shadow] hover:border-foreground/20 hover:shadow-md",
                archived && "opacity-80",
              )}
            >
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <FirmLogo
                    src={acc.firmLogo}
                    name={acc.firmName}
                    className="h-6 w-6 shrink-0"
                  />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-foreground">
                      {acc.firmName}
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span className="font-mono">#{acc.slug}</span>
                      {acc.data.accountLabel && (
                        <span className="truncate">
                          · {acc.data.accountLabel}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <StatusBadge
                  status={acc.tableStatus}
                  label={acc.data.statusLabel}
                />
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
                <div>
                  <span className="text-muted-foreground">Size</span>
                  <div className="mt-0.5 font-semibold">
                    {formatSize(acc.accountSize)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">
                    {archived ? "Final" : "Balance"}
                  </span>
                  <div
                    className={cn(
                      "mt-0.5 font-mono",
                      archived
                        ? "text-muted-foreground"
                        : "font-semibold text-accent",
                    )}
                  >
                    {formatBalance(displayBalance)}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Type</span>
                  <div className="mt-0.5 font-medium">
                    {CATEGORY_LABEL[cat]}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Phase</span>
                  <div className="mt-0.5">
                    <Badge variant={phaseBadgeVariant(acc.phase)} size="xs">
                      {PHASE_LABEL[acc.phase] ?? acc.phase}
                    </Badge>
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Rules</span>
                  <div className="mt-0.5">
                    {archived ? (
                      <span className="text-xs text-muted-foreground/60">—</span>
                    ) : acc.rulesConfirmedAt != null ? (
                      <Badge variant="passed" size="xs">
                        <Check className="size-2.5" strokeWidth={3} />
                        Confirmed
                      </Badge>
                    ) : (
                      <Badge variant="warning" size="xs">Pending</Badge>
                    )}
                  </div>
                </div>
                <div>
                  <span className="text-muted-foreground">Pair</span>
                  <div className="mt-0.5">
                    {acc.pairSlug ? (
                      <Link
                        to={`/pairs/${encodeURIComponent(acc.pairSlug)}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center rounded-full bg-accent/10 px-2 py-0.5 font-mono text-[10px] text-accent"
                      >
                        {acc.pairSlug}
                      </Link>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="py-12 text-center text-sm text-muted-foreground"
        >
          No accounts found for selected filters.
        </motion.p>
      )}

      <Pagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        className="mt-6"
      />
    </div>
  );
}
